/**
 * Soft monthly message caps for Ask Chckvet (browser-local for now).
 *
 * Not a security boundary — a determined user can clear localStorage.
 * Server-side spend caps should replace this later.
 */

export const ASK_CHCKVET_USER_MONTHLY_LIMIT = 50
export const ASK_CHCKVET_CLINIC_MONTHLY_LIMIT = 100

const STORAGE_PREFIX = 'chckup.askChckvet.usage.v1'

export type AskChckvetUsage = {
    monthKey: string
    userCount: number
    clinicCount: number
    userRemaining: number
    clinicRemaining: number
    /** Which limit is exhausted, if any. User limit is checked first. */
    blockedBy: 'user' | 'clinic' | null
}

/** Local calendar month `YYYY-MM` (clinic staff timezone). */
export function askChckvetMonthKey(now: Date = new Date()): string {
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    return `${year}-${month}`
}

function userStorageKey(monthKey: string, userId: string): string {
    return `${STORAGE_PREFIX}:${monthKey}:user:${userId}`
}

function clinicStorageKey(monthKey: string, clinicId: string): string {
    return `${STORAGE_PREFIX}:${monthKey}:clinic:${clinicId}`
}

function readCount(storageKey: string): number {
    if (typeof window === 'undefined') return 0
    try {
        const raw = window.localStorage.getItem(storageKey)
        if (!raw) return 0
        const n = Number(raw)
        return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
    } catch {
        return 0
    }
}

function writeCount(storageKey: string, count: number): void {
    if (typeof window === 'undefined') return
    try {
        window.localStorage.setItem(storageKey, String(Math.max(0, count)))
    } catch {
        // Quota / privacy mode — ignore; send path still works this session.
    }
}

export function getAskChckvetUsage(
    userId: string,
    clinicId: string,
    now: Date = new Date(),
): AskChckvetUsage {
    const monthKey = askChckvetMonthKey(now)
    const userCount = readCount(userStorageKey(monthKey, userId))
    const clinicCount = readCount(clinicStorageKey(monthKey, clinicId))
    const userRemaining = Math.max(
        0,
        ASK_CHCKVET_USER_MONTHLY_LIMIT - userCount,
    )
    const clinicRemaining = Math.max(
        0,
        ASK_CHCKVET_CLINIC_MONTHLY_LIMIT - clinicCount,
    )
    let blockedBy: AskChckvetUsage['blockedBy'] = null
    if (userRemaining <= 0) blockedBy = 'user'
    else if (clinicRemaining <= 0) blockedBy = 'clinic'

    return {
        monthKey,
        userCount,
        clinicCount,
        userRemaining,
        clinicRemaining,
        blockedBy,
    }
}

/**
 * Human-readable block message when a cap is hit. Null when send is allowed.
 */
export function askChckvetUsageBlockMessage(
    usage: AskChckvetUsage,
): string | null {
    if (usage.blockedBy === 'user') {
        return `You've reached the monthly Ask Chckvet limit (${ASK_CHCKVET_USER_MONTHLY_LIMIT} messages). It resets next month.`
    }
    if (usage.blockedBy === 'clinic') {
        return `This clinic has reached the monthly Ask Chckvet limit (${ASK_CHCKVET_CLINIC_MONTHLY_LIMIT} messages). It resets next month.`
    }
    return null
}

/** Increment user + clinic counters after a successful send. */
export function recordAskChckvetMessage(
    userId: string,
    clinicId: string,
    now: Date = new Date(),
): AskChckvetUsage {
    const monthKey = askChckvetMonthKey(now)
    const nextUser = readCount(userStorageKey(monthKey, userId)) + 1
    const nextClinic = readCount(clinicStorageKey(monthKey, clinicId)) + 1
    writeCount(userStorageKey(monthKey, userId), nextUser)
    writeCount(clinicStorageKey(monthKey, clinicId), nextClinic)
    return getAskChckvetUsage(userId, clinicId, now)
}
